import { useEffect, useSyncExternalStore } from "react";

/**
 * Tiny external store counting how many mounted screens have asked for the
 * feedback tab to be hidden. The tab (see `FeedbackWidget`) subscribes and
 * renders nothing while the count is above zero.
 */
let hideRequests = 0;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): boolean {
  return hideRequests > 0;
}

/** Whether any screen currently asks for the feedback tab to be hidden. */
export function useFeedbackWidgetHidden(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Hides the feedback tab while `active` is true (for example on the mobile
 * calendar, where a floating tab would sit over the timeline). Restores it on
 * unmount or when `active` flips back to false.
 */
export function useHideFeedbackWidget(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    hideRequests += 1;
    emit();
    return () => {
      hideRequests -= 1;
      emit();
    };
  }, [active]);
}
